import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Building2, 
  Users,
  Globe,
  Phone,
  Mail,
  RefreshCw,
  FolderOpen,
  Search,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { Row, Col, Button, Table, Card, Badge, Dropdown, Pagination, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { apiService } from '../services/api';
import ImprovedPagination from './common/ImprovedPagination';
import './Companies.css';
import './Dashboard.css';

const SEARCH_DEBOUNCE_MS = 400;

const Companies = () => {
  const { user } = useUser();
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const STORAGE_KEY = 'companiesListState';
  /** True while the paginated list is fetching (initial load, refresh, page/size change, clear search). Never used to hide the search bar. */
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    totalCount: 0,
    totalPages: 0,
    page: 1,
    limit: 10
  });
  const [sortColumn, setSortColumn] = useState(null); // 'identificationNumber' | 'name' | 'status' | 'email'
  const [sortDirection, setSortDirection] = useState('asc');
  const hasRestoredState = useRef(false);
  const searchDebounceRef = useRef(null);
  const performSearchRef = useRef(null);
  /** Keeps last input value for ignoring stale search responses after the user clears or edits the query. */
  const latestSearchInputRef = useRef('');

  const navigate = useNavigate();

  useEffect(() => {
    return () => clearTimeout(searchDebounceRef.current);
  }, []);

  useEffect(() => {
    // Force Poppins font on all elements in this component
    const style = document.createElement('style');
    style.textContent = `
      .companies-wrapper *,
      .companies-wrapper *::before,
      .companies-wrapper *::after {
        font-family: 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const coerceNumber = (val, fallback) => {
    const n = typeof val === 'number' ? val : Number(val);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };

  const fetchCompanies = async (page = currentPage, limit = itemsPerPage, source = 'unknown') => {
    try {
      setListLoading(true);
      setError(null);
      const safePage = page && page.nativeEvent ? currentPage : coerceNumber(page, currentPage);
      const safeLimit = coerceNumber(limit, itemsPerPage);
      console.log('fetchCompanies called with params:', { source, page, limit, safePage, safeLimit, currentPage });
      // Use proper pagination - fetch only the requested page
      const response = await apiService.getCompanies(safePage, safeLimit);
      
      if (response.success) {
        // Prefer requested page/limit when API doesn't echo them back
        const resolvedPage = coerceNumber(page ?? response.page, safePage);
        const resolvedLimit = coerceNumber(limit ?? response.limit, safeLimit);
        console.log('fetchCompanies response meta:', {
          source,
          responsePage: response.page,
          responseLimit: response.limit,
          resolvedPage,
          resolvedLimit
        });
        // Transform API response to match component's expected structure
        const transformedCompanies = response.data.map((company) => {
          return {
            id: company.id,
            identificationNumber: company.identificationNo || `COMP${company.id.toString().padStart(3, '0')}`,
            name: company.company || 'Unknown Company',
            industry: company.branch || 'Unknown Industry',
            status: company.active ? 'Active' : 'Inactive',
            employees: company.userCount || 0,
            website: company.urlslug ? `www.${company.urlslug}.com` : '',
            email: company.email || '',
            phone: company.coPhone || '',
            address: `${company.coAddress || ''}, ${company.coCity || ''}, ${company.coState || ''} ${company.coZip || ''}`.trim(),
            projects: 3, // Mock project count for now - will be updated with real API call
            users: company.userCount || 0,
            revenue: company.aragingCurrent || 0,
            founded: 'N/A'
          };
        });
        
        setCompanies(transformedCompanies);
        // For pagination, we only keep current page data
        // allCompanies is used for client-side sorting, but with proper pagination
        // we should rely on backend sorting or fetch all when sorting is needed
        const totalCount = response.totalCount ?? 0;
        const totalPages = response.totalPages ?? Math.max(1, Math.ceil(totalCount / resolvedLimit));
        setPaginationInfo({
          totalCount,
          totalPages,
          page: resolvedPage,
          limit: resolvedLimit
        });
        console.log('setting currentPage to resolvedPage:', resolvedPage, 'source:', source);
        setCurrentPage(resolvedPage);
      } else {
        setError(response.message || 'Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError(error.message || 'Failed to fetch companies');
    } finally {
      setListLoading(false);
    }
  };

  const performSearch = async (query) => {
    if (!query || !query.trim()) {
      // If search query is empty, fetch regular companies
      await fetchCompanies(1);
      setSearchResults([]);
      setIsSearching(false);
      setIsSearchLoading(false);
      return;
    }

    const trimmed = query.trim();
    try {
      setIsSearchLoading(true);
      setIsSearching(true);
      setError(null);
      const response = await apiService.searchCompanies(trimmed);

      if (trimmed !== (latestSearchInputRef.current || '').trim()) {
        return;
      }

      if (response.success) {
        // Transform search results to match component's expected structure
        const transformedResults = response.data.map((company) => {
          return {
            id: company.id,
            identificationNumber: company.identificationNo || `COMP${company.id.toString().padStart(3, '0')}`,
            name: company.company || 'Unknown Company',
            industry: company.branch || 'Unknown Industry',
            status: company.active ? 'Active' : 'Inactive',
            employees: 0, // User count not available in search results
            website: company.urlslug ? `www.${company.urlslug}.com` : '',
            email: company.email || '',
            phone: company.coPhone || '',
            address: `${company.coAddress || ''}, ${company.coCity || ''}, ${company.coState || ''} ${company.coZip || ''}`.trim(),
            projects: 0, // Project count not available in search results
            users: 0,
            revenue: company.aragingCurrent || 0,
            founded: 'N/A'
          };
        });
        
        setSearchResults(transformedResults);
        setIsSearching(true);
      } else {
        setError(response.message || 'Failed to search companies');
        setSearchResults([]);
      }
    } catch (error) {
      if (trimmed !== (latestSearchInputRef.current || '').trim()) {
        return;
      }
      console.error('Error searching companies:', error);
      setError(error.message || 'Failed to search companies');
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  performSearchRef.current = performSearch;

  const debouncedSearch = useCallback((query) => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      performSearchRef.current(query);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  const handleSearch = async () => {
    await performSearch(searchQuery);
  };

  const clearSearch = async () => {
    clearTimeout(searchDebounceRef.current);
    latestSearchInputRef.current = '';
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setIsSearchLoading(false);
    await fetchCompanies(1);
  };

  // Restore saved table state and fetch data accordingly
  useEffect(() => {
    let savedPage = 1;
    let savedItemsPerPage = itemsPerPage;
    let savedSearch = '';

    const savedRaw = sessionStorage.getItem(STORAGE_KEY);
    if (savedRaw) {
      try {
        const saved = JSON.parse(savedRaw);
        savedPage = coerceNumber(saved.currentPage, savedPage);
        savedItemsPerPage = coerceNumber(saved.itemsPerPage, savedItemsPerPage);
        savedSearch = saved.searchQuery || '';
      } catch (err) {
        console.error('Failed to parse saved companies state', err);
      }
    }

    // Prevent double fetch in React strict/dev double effects
    if (hasRestoredState.current) return;
    hasRestoredState.current = true;

    setItemsPerPage(savedItemsPerPage);
    setCurrentPage(savedPage);
    
    // Clear search state when navigating back (don't restore search query)
    // This prevents infinite loading when returning from a company page
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setIsSearchLoading(false);
    
    // Always fetch regular companies list when component mounts
    fetchCompanies(savedPage, savedItemsPerPage, 'restore-state');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteCompany = (companyId) => {
    setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== companyId));
  };

  const handleViewProjects = (companyId) => {
    navigate(`/company/${companyId}/projects`);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (isSearching) return;
    fetchCompanies(page, itemsPerPage, 'page-change');
  };

  // Handle items per page change
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    if (isSearching) return;
    fetchCompanies(1, newItemsPerPage, 'items-per-page-change');
  };

  // Handle refresh
  const handleRefresh = () => {
    if (isSearching) {
      performSearch(searchQuery);
      return;
    }
    fetchCompanies(currentPage, itemsPerPage, 'refresh');
  };

  // Sorting helpers
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="text-muted ms-1" />;
    return sortDirection === 'asc'
      ? <ArrowUp size={14} className="text-primary ms-1" />
      : <ArrowDown size={14} className="text-primary ms-1" />;
  };

  const sortedCompanies = React.useMemo(() => {
    // With proper pagination, use current page companies or search results
    // For search results, we may have all matching results, so sort those
    // For regular pagination, sorting should be done on backend, but we can sort current page
    const source = searchResults.length > 0 ? searchResults : companies;
    const list = [...source];
    if (!sortColumn) return list;

    return list.sort((a, b) => {
      const aVal = (a[sortColumn] || '').toString().toLowerCase();
      const bVal = (b[sortColumn] || '').toString().toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [companies, searchResults, sortColumn, sortDirection]);

  // Use backend pagination - companies are already paginated from API
  // Only apply client-side pagination for search results which may contain all matches
  const totalItems = paginationInfo.totalCount || (searchResults.length > 0 ? searchResults.length : sortedCompanies.length);
  const totalPagesClient = paginationInfo.totalPages || Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentPageSafe = Math.min(currentPage, totalPagesClient);
  
  // If we have search results, they may not be paginated, so slice them
  // Otherwise, use the already-paginated companies from backend
  const paginatedCompanies = searchResults.length > 0 
    ? sortedCompanies.slice(
        (currentPageSafe - 1) * itemsPerPage,
        currentPageSafe * itemsPerPage
      )
    : sortedCompanies;

  // Persist table state for this session (guard against non-serializable values)
  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          currentPage: coerceNumber(currentPage, 1),
          itemsPerPage: coerceNumber(itemsPerPage, 25),
          searchQuery: typeof searchQuery === 'string' ? searchQuery : ''
        })
      );
    } catch (err) {
      console.error('Failed to persist companies table state', err);
    }
  }, [currentPage, itemsPerPage, searchQuery, STORAGE_KEY]);


  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return <Badge bg="success">{status}</Badge>;
      case 'Inactive':
        return <Badge bg="secondary">{status}</Badge>;
      case 'Pending':
        return <Badge bg="warning" text="dark">{status}</Badge>;
      default:
        return <Badge bg="light" text="dark">{status}</Badge>;
    }
  };

  return (
    <div className="companies-wrapper">
      <div className="modern-dashboard">
        {/* Header - Only show for companyAdmin, hide for superAdmin and regular users */}
        {user?.role === 'companyAdmin' && (
          <div className="dashboard-header-section mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dashboard-title">Company Management</h1>
                <p className="dashboard-subtitle">Manage your client companies and partnerships</p>
              </div>
              <div className="dashboard-actions">
              <Button 
                variant="outline-light" 
                className="me-2"
                onClick={handleRefresh}
                disabled={listLoading || isSearchLoading}
              >
                <RefreshCw size={16} className={`me-2 ${listLoading || isSearchLoading ? 'spin' : ''}`} />
                {listLoading || isSearchLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <div className="d-flex align-items-center">
              <div className="me-3">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <div>
                <strong>Error loading companies:</strong> {error}
                <Button 
                  variant="outline-danger" 
                  size="sm" 
                  className="ms-3"
                  onClick={() => fetchCompanies(currentPage, itemsPerPage, 'error-try-again')}
                >
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        )}

        <>
            {/* Stats Cards - Hide for superAdmin */}
            {user?.role !== 'superAdmin' && (
              <Row className="mb-4 g-3">
                <Col xl={3} lg={4} md={6} className="mb-3">
                  <Card className="modern-stat-card h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="stat-content">
                          <p className="stat-title">Total Companies</p>
                          <h3 className="stat-value">{paginationInfo.totalCount}</h3>
                          <div className="stat-change">
                            <span className="change-text">All time</span>
                          </div>
                        </div>
                        <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
                          <Building2 className="text-white" size={24} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={4} md={6} className="mb-3">
                  <Card className="modern-stat-card h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="stat-content">
                          <p className="stat-title">Active Companies</p>
                          <h3 className="stat-value">{companies.filter(company => company.status === 'Active').length}</h3>
                          <div className="stat-change">
                            <span className="change-text">Currently active</span>
                          </div>
                        </div>
                        <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                          <Globe className="text-white" size={24} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={4} md={6} className="mb-3">
                  <Card className="modern-stat-card h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="stat-content">
                          <p className="stat-title">Total Projects</p>
                          <h3 className="stat-value">{companies.reduce((sum, company) => sum + company.projects, 0)}</h3>
                          <div className="stat-change">
                            <span className="change-text">Across all companies</span>
                          </div>
                        </div>
                        <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
                          <FolderOpen className="text-white" size={24} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={3} lg={4} md={6} className="mb-3">
                  <Card className="modern-stat-card h-100">
                    <Card.Body>
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="stat-content">
                          <p className="stat-title">Total Users</p>
                          <h3 className="stat-value">{companies.reduce((sum, company) => sum + company.users, 0)}</h3>
                          <div className="stat-change">
                            <span className="change-text">Across all companies</span>
                          </div>
                        </div>
                        <div className="stat-icon-wrapper" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                          <Users className="text-white" size={24} />
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Search Bar */}
            <Card className="mb-4">
              <Card.Body>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control form-control-lg ps-5"
                    placeholder="Search companies by name, branch, city, or state..."
                    value={searchQuery}
                    autoComplete="off"
                    onChange={(e) => {
                      const value = e.target.value;
                      latestSearchInputRef.current = value;
                      setSearchQuery(value);
                      if (!value.trim()) {
                        clearTimeout(searchDebounceRef.current);
                        setSearchResults([]);
                        setIsSearching(false);
                        setIsSearchLoading(false);
                        fetchCompanies(1);
                        return;
                      }
                      debouncedSearch(value);
                    }}
                  />
                  <Search className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" size={20} aria-hidden />
                  {searchQuery && (
                    <Button
                      variant="link"
                      className="position-absolute top-50 end-0 translate-middle-y p-0 me-3"
                      onClick={clearSearch}
                      style={{ color: '#6c757d' }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
                {isSearchLoading && (
                  <div className="d-flex align-items-center gap-2 mt-3 text-muted small" role="status" aria-live="polite">
                    <div className="spinner-border spinner-border-sm text-primary" role="presentation" />
                    <span>
                      Searching companies
                      {searchQuery.trim() ? ` for “${searchQuery.trim()}”` : ''}…
                    </span>
                  </div>
                )}
                {isSearching && !isSearchLoading && searchQuery.trim() !== '' && (
                  <div className="mt-3">
                    <div className="alert alert-info mb-0 py-2 small">
                      <Search size={16} className="me-2 align-text-bottom" aria-hidden />
                      <strong>Search results:</strong> {searchResults.length} companies matching &quot;{searchQuery.trim()}&quot;
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Companies Table */}
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  {isSearching && searchQuery.trim() !== '' ? 'Search Results' : 'Companies List'}
                </h5>
                <span className="text-muted">
                  {isSearching && searchQuery.trim() !== ''
                    ? `${searchResults.length} results for "${searchQuery.trim()}"`
                    : `Page ${currentPageSafe} of ${totalPagesClient} (${totalItems} total)`
                  }
                </span>
              </Card.Header>
              <Card.Body>
                <Table responsive className="modern-table">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('identificationNumber')}>
                        <div className="d-flex align-items-center">
                          # No. {getSortIcon('identificationNumber')}
                        </div>
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                        <div className="d-flex align-items-center">
                          Company Name {getSortIcon('name')}
                        </div>
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                        <div className="d-flex align-items-center">
                          Contact (Email) {getSortIcon('email')}
                        </div>
                      </th>
                      <th style={{ cursor: 'pointer', textAlign: 'right' }} onClick={() => handleSort('status')}>
                        <div className="d-flex align-items-center justify-content-end">
                          Status {getSortIcon('status')}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {listLoading && !isSearching ? (
                      <tr>
                        <td colSpan={4} className="text-center py-5 text-muted">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading companies</span>
                          </div>
                          <p className="mt-3 mb-0 small">Loading companies…</p>
                        </td>
                      </tr>
                    ) : isSearchLoading ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted small">
                          Updating results…
                        </td>
                      </tr>
                    ) : paginatedCompanies.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          No companies to display.
                        </td>
                      </tr>
                    ) : (
                      paginatedCompanies.map((company) => (
                        <tr 
                          key={company.id}
                          className="cursor-pointer"
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleViewProjects(company.id)}
                        >
                          <td>
                            <Badge bg="light" text="dark" className="fw-bold">
                              {company.identificationNumber}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{company.name}</div>
                          </td>
                          <td>
                            <div className="small">
                              <div className="d-flex align-items-center mb-1">
                                <Mail size={12} className="me-1 text-muted" />
                                {company.email ? (
                                  <a 
                                    href={`mailto:${company.email}`} 
                                    style={{ color: '#0d6efd', textDecoration: 'none' }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {company.email}
                                  </a>
                                ) : (
                                  <span className="text-muted">N/A</span>
                                )}
                              </div>
                              <div className="d-flex align-items-center">
                                <Phone size={12} className="me-1 text-muted" />
                                {company.phone}
                              </div>
                            </div>
                          </td>
                          <td style={{ textAlign: 'right' }}>{getStatusBadge(company.status)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>

                {/* Pagination */}
                {(paginationInfo.totalPages > 1 || true) && (
                  <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                    {/* Items per page selector */}
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        Show:
                      </span>
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                        size="sm"
                        style={{ width: 'auto', minWidth: '80px' }}
                        disabled={listLoading || isSearchLoading}
                      >
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </Form.Select>
                      <span className="text-muted" style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        per page
                      </span>
                    </div>
                    
                    {/* Pagination controls */}
                    {totalPagesClient > 1 && (
                      <Pagination className="mb-0">
                      <Pagination.First 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPageSafe === 1 || listLoading || isSearchLoading}
                      />
                      <Pagination.Prev 
                        onClick={() => handlePageChange(currentPageSafe - 1)}
                        disabled={currentPageSafe === 1 || listLoading || isSearchLoading}
                      />
                      
                      {/* Show page numbers with ellipsis */}
                      {Array.from({ length: totalPagesClient }, (_, i) => i + 1).map(page => {
                        // Show first 3 pages, last 3 pages, and current page with context
                        const shouldShow = 
                          page <= 3 || 
                          page >= totalPagesClient - 2 || 
                          Math.abs(page - currentPageSafe) <= 1;
                        
                        if (!shouldShow) {
                          // Show ellipsis for gaps
                          if (page === 4 && currentPageSafe > 5) {
                            return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                          }
                          if (page === totalPagesClient - 3 && currentPageSafe < totalPagesClient - 4) {
                            return <Pagination.Ellipsis key={`ellipsis-${page}`} />;
                          }
                          return null;
                        }
                        
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPageSafe}
                            onClick={() => handlePageChange(page)}
                            disabled={listLoading || isSearchLoading}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      })}
                      
                      <Pagination.Next 
                        onClick={() => handlePageChange(currentPageSafe + 1)}
                        disabled={currentPageSafe === totalPagesClient || listLoading || isSearchLoading}
                      />
                      <Pagination.Last 
                        onClick={() => handlePageChange(totalPagesClient)}
                        disabled={currentPageSafe === totalPagesClient || listLoading || isSearchLoading}
                      />
                      </Pagination>
                    )}
                  </div>
                )}
              </Card.Body>
            </Card>
        </>

      </div>
    </div>
  );
};

export default Companies; 